import { useEffect, useRef, useReducer, useCallback, useState } from "react";
import { getLiveSocket } from "../../lib/socket.js";
import { useCountUp } from "../../lib/useCountUp.js";
import { useRotatingWindow } from "../../lib/useRotatingWindow.js";
import { CountdownRing } from "../../components/CountdownRing.js";
import { toLocalPhaseEnd } from "../../lib/server-clock.js";
import { OptionShape } from "../../components/OptionShape.js";
import { useT, type Translate } from "../../i18n/index.js";
import { PodiumCeremony } from "./PodiumCeremony.js";
import type { Socket } from "socket.io-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AnswerOption {
  id: string;
  label: string;
}

interface QuestionEvent {
  questionRunId: string;
  type: string;
  prompt: string;
  imageAssetId?: string | null;
  timerSeconds: number;
  orderIndex: number;
  options: AnswerOption[];
}

interface LeaderboardEntry {
  rank: number;
  playerId: string;
  nickname: string;
  score: number;
}

interface Answerer {
  playerId: string;
  nickname: string;
  /** Absent for players who never answered; there is no time to show. */
  responseTimeMs?: number;
}

interface TextResultSummary {
  submittedText: string | null;
  isCorrect: boolean;
  count: number;
  answerers?: Answerer[];
}

type GamePhase =
  | "question_intro"
  | "answering"
  | "answer_reveal"
  | "leaderboard"
  | "podium"
  | "ended"
  | "aborted";

interface HostGameState {
  phase: GamePhase;
  question: QuestionEvent | null;
  answerCount: number;
  totalActive: number;
  distribution: Record<string, number> | null;
  optionAnswerers: Record<string, Answerer[]>;
  correctOptionIds: string[];
  correctTextAnswers: string[];
  textResults: TextResultSummary[];
  omittedTextResults: number;
  noAnswer: Answerer[];
  noAnswerCount: number;
  leaderboard: LeaderboardEntry[];
  phaseEndsAt: number | null;
  questionCount: number;
  questionIndex: number;
  showLeaderboard: boolean;
}

type Action =
  | { type: "STATE_CHANGED"; phase: GamePhase; phaseEndsAt?: number | null }
  | { type: "QUESTION_STARTED"; question: QuestionEvent; questionCount: number; questionIndex: number }
  | { type: "TIMER"; phaseEndsAt: number | null }
  | { type: "ANSWER_PROGRESS"; answered: number; total: number }
  | {
      type: "ANSWER_DISTRIBUTION";
      distribution: Record<string, number>;
      optionAnswerers?: Record<string, Answerer[]>;
      correctOptionIds: string[];
      correctTextAnswers?: string[];
      textResults?: TextResultSummary[];
      omittedTextResults?: number;
      noAnswer?: Answerer[];
      noAnswerCount?: number;
    }
  | { type: "LEADERBOARD"; entries: LeaderboardEntry[] }
  | { type: "PODIUM"; entries: LeaderboardEntry[] }
  | { type: "SETTINGS"; showLeaderboard?: boolean };

function reducer(state: HostGameState, action: Action): HostGameState {
  switch (action.type) {
    case "STATE_CHANGED":
      return {
        ...state,
        phase: action.phase,
        phaseEndsAt: action.phaseEndsAt ?? null,
      };
    case "QUESTION_STARTED":
      return {
        ...state,
        question: action.question,
        phase: "question_intro",
        answerCount: 0,
        totalActive: state.totalActive,
        distribution: null,
        optionAnswerers: {},
        correctOptionIds: [],
        correctTextAnswers: [],
        textResults: [],
        omittedTextResults: 0,
        noAnswer: [],
        noAnswerCount: 0,
        questionCount: action.questionCount,
        questionIndex: action.questionIndex,
      };
    case "TIMER":
      return {
        ...state,
        phase: "answering",
        phaseEndsAt: action.phaseEndsAt,
      };
    case "ANSWER_PROGRESS":
      return { ...state, answerCount: action.answered, totalActive: action.total };
    case "ANSWER_DISTRIBUTION":
      return {
        ...state,
        phase: "answer_reveal",
        distribution: action.distribution,
        optionAnswerers: action.optionAnswerers ?? {},
        correctOptionIds: action.correctOptionIds,
        correctTextAnswers: action.correctTextAnswers ?? [],
        textResults: action.textResults ?? [],
        omittedTextResults: action.omittedTextResults ?? 0,
        noAnswer: action.noAnswer ?? [],
        noAnswerCount: action.noAnswerCount ?? 0,
      };
    case "LEADERBOARD":
      return { ...state, phase: "leaderboard", leaderboard: action.entries };
    case "PODIUM":
      return { ...state, phase: "podium", leaderboard: action.entries };
    case "SETTINGS":
      return {
        ...state,
        showLeaderboard: action.showLeaderboard ?? state.showLeaderboard,
      };
    default:
      return state;
  }
}

const INITIAL: HostGameState = {
  phase: "question_intro",
  question: null,
  answerCount: 0,
  totalActive: 0,
  distribution: null,
  optionAnswerers: {},
  correctOptionIds: [],
  correctTextAnswers: [],
  textResults: [],
  omittedTextResults: 0,
  noAnswer: [],
  noAnswerCount: 0,
  leaderboard: [],
  phaseEndsAt: null,
  questionCount: 0,
  questionIndex: 0,
  showLeaderboard: true,
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const OPTION_COLORS = [
  "var(--opt-0)",
  "var(--opt-1)",
  "var(--opt-2)",
  "var(--opt-3)",
];

const s = {
  // The shared screen is the whole viewport: fixed height, never scrolled by
  // hand. Whatever does not fit is the board's problem, and it drifts instead.
  page: {
    height: "100svh",
    overflow: "hidden",
    background: "var(--bg)",
    color: "var(--text)",
    display: "flex",
    flexDirection: "column" as const,
    fontFamily: "inherit",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.875rem var(--space-lg)",
    background: "var(--surface)",
    borderBottom: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
  },
  headerLeft: {
    fontWeight: 700,
    fontSize: "var(--text-base)",
    color: "var(--text)",
  },
  headerRight: {
    fontSize: "var(--text-sm)",
    color: "var(--text-mid)",
    fontWeight: 500,
  },
  main: {
    flex: 1,
    // Lets the board inside actually shrink; without it a flex child refuses
    // to go below its content height and the page grows a scrollbar instead.
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
    // Tracks the screen rather than a fixed column, so a television is not
    // showing a laptop-sized strip down the middle.
    maxWidth: "min(1600px, 94vw)",
    width: "100%",
    margin: "0 auto",
    padding: "clamp(0.75rem, 2vh, 2rem) clamp(1rem, 3vw, 3rem)",
    gap: "clamp(0.75rem, 2vh, 2rem)",
  },
  prompt: {
    margin: 0,
    fontSize: "clamp(1.5rem, 3.2vw, 3.25rem)",
    fontWeight: 800,
    textAlign: "center" as const,
    lineHeight: 1.25,
    color: "var(--text)",
  },
  questionImage: {
    width: "100%",
    maxHeight: "320px",
    objectFit: "cover" as const,
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
  },
  answerProgress: {
    textAlign: "center" as const,
    color: "var(--text-mid)",
    fontSize: "var(--text-base)",
    fontWeight: 500,
  },
  timerBadge: {
    color: "var(--brand)",
    fontWeight: 800,
    whiteSpace: "nowrap" as const,
  },
  optionBar: (color: string, isCorrect: boolean | null) => ({
    background: isCorrect === true ? "var(--success)" : isCorrect === false ? "var(--bg-deep)" : color,
    borderRadius: "var(--radius-md)",
    padding: "var(--space-md) var(--space-md)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
    border: isCorrect === true ? "2px solid var(--success)" : "2px solid transparent",
    opacity: isCorrect === false ? 0.55 : 1,
    minHeight: "72px",
  }),
  optionLeft: {
    display: "flex",
    alignItems: "center",
    gap: "0.875rem",
  },
  correctBadge: {
    flexShrink: 0,
    padding: "0.2rem 0.6rem",
    borderRadius: "2rem",
    background: "rgba(255,255,255,0.9)",
    color: "var(--success)",
    fontSize: "var(--text-xs)",
    fontWeight: 800,
    letterSpacing: "0.03em",
    textTransform: "uppercase" as const,
  },
  optionLetter: {
    flexShrink: 0,
    width: "2.25rem",
    height: "2.25rem",
    borderRadius: "0.5rem",
    background: "rgba(255,255,255,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 800,
    fontSize: "1rem",
    color: "#fff",
  },
  optionLabel: {
    fontSize: "clamp(1rem, 1.5vw, 1.75rem)",
    fontWeight: 700,
    color: "#fff",
  },
  optionCount: {
    background: "rgba(255,255,255,0.25)",
    borderRadius: "2rem",
    padding: "0.25rem 0.875rem",
    fontWeight: 800,
    minWidth: "2rem",
    textAlign: "center" as const,
    color: "#fff",
    fontSize: "var(--text-sm)",
  },
  textAnswerList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "0.75rem",
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  optionBlock: {
    display: "flex",
    flexDirection: "column" as const,
  },
  // Fixed height plus hidden overflow: however many chips arrive, the strip
  // can never push the answer grid off a shared display.
  answererStrip: {
    display: "flex",
    flexWrap: "wrap" as const,
    // Centred under the bar the names pour out of. Left-aligned, the column
    // hangs off one corner instead of falling from the whole answer.
    justifyContent: "center",
    gap: "0.375rem",
    listStyle: "none",
    margin: 0,
    padding: "0.5rem 0.125rem 0",
    maxHeight: "5.5rem",
    overflow: "hidden",
  },
  answererChip: {
    display: "flex",
    alignItems: "baseline",
    gap: "0.375rem",
    maxWidth: "11rem",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "2rem",
    padding: "0.125rem 0.625rem",
    fontSize: "var(--text-xs)",
    lineHeight: 1.6,
  },
  answererName: {
    fontWeight: 700,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  answererTime: {
    color: "var(--text-mid)",
    fontWeight: 600,
    flexShrink: 0,
    whiteSpace: "nowrap" as const,
  },
  noAnswerBlock: {
    borderTop: "1px solid var(--border)",
    paddingTop: "var(--space-md)",
  },
  noAnswerHeading: {
    display: "flex",
    alignItems: "center",
    // Matches the centred strip beneath it.
    justifyContent: "center",
    gap: "0.5rem",
    margin: 0,
    fontSize: "var(--text-sm)",
    fontWeight: 700,
    color: "var(--text-mid)",
  },
  noAnswerCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "1.5rem",
    padding: "0.05rem 0.45rem",
    borderRadius: "2rem",
    background: "var(--bg-deep)",
    color: "var(--text)",
    fontSize: "var(--text-xs)",
    fontWeight: 800,
  },
  answererMore: {
    margin: "0.25rem 0 0",
    textAlign: "center" as const,
    fontSize: "var(--text-xs)",
    fontWeight: 700,
    lineHeight: 1.6,
    color: "var(--text-mid)",
  },
  textAnswerRow: (isCorrect: boolean) => ({
    background: isCorrect ? "var(--success-lt)" : "var(--surface)",
    border: `1.5px solid ${isCorrect ? "var(--success)" : "var(--border)"}`,
    borderRadius: "var(--radius-md)",
    padding: "1rem var(--space-lg)",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "stretch",
    gap: "var(--space-xs)",
    boxShadow: "var(--shadow-sm)",
  }),
  textAnswerCorrectBadge: {
    marginLeft: "0.625rem",
    padding: "0.15rem 0.55rem",
    borderRadius: "2rem",
    background: "var(--success)",
    color: "oklch(99% 0.006 70)",
    fontSize: "var(--text-xs)",
    fontWeight: 800,
    letterSpacing: "0.03em",
    textTransform: "uppercase" as const,
    verticalAlign: "middle",
  },
  textAnswerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "var(--space-md)",
  },
  textAnswerValue: {
    fontWeight: 800,
    fontSize: "var(--text-base)",
  },
  textAnswerCount: {
    color: "var(--text-mid)",
    fontWeight: 700,
    whiteSpace: "nowrap" as const,
  },
  acceptedAnswers: {
    margin: 0,
    textAlign: "center" as const,
    color: "var(--text-mid)",
    fontSize: "var(--text-base)",
  },
  // Ten rows, sized off the viewport height so they fit a short laptop screen
  // and still fill a television without needing to move.
  leaderboardList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "clamp(0.3rem, 0.9vh, 0.75rem)",
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  leaderboardRow: (rank: number) => {
    const medalBg = rank === 1
      ? "oklch(90% 0.08 80)"
      : rank === 2
      ? "oklch(88% 0.01 65)"
      : rank === 3
      ? "oklch(86% 0.09 50)"
      : "var(--surface)";
    const medalBorder = rank === 1
      ? "oklch(70% 0.16 80)"
      : rank === 2
      ? "oklch(65% 0.04 65)"
      : rank === 3
      ? "oklch(62% 0.14 50)"
      : "var(--border)";
    return {
      display: "flex",
      alignItems: "center",
      gap: "var(--space-md)",
      padding: "clamp(0.45rem, 1.4vh, 1.1rem) clamp(0.9rem, 2vw, 2rem)",
      background: medalBg,
      borderRadius: "var(--radius-md)",
      border: `1.5px solid ${medalBorder}`,
      boxShadow: "var(--shadow-sm)",
    };
  },
  leaderboardRank: {
    fontWeight: 800,
    fontSize: "clamp(1.1rem, 1.6vw, 1.9rem)",
    minWidth: "3rem",
    color: "var(--text-mid)",
  },
  leaderboardNickname: {
    flex: 1,
    fontSize: "clamp(1rem, 1.5vw, 1.75rem)",
    fontWeight: 600,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  leaderboardScore: {
    fontWeight: 800,
    color: "var(--brand)",
    fontSize: "clamp(1rem, 1.5vw, 1.75rem)",
  },
  boardFooter: {
    margin: 0,
    textAlign: "center" as const,
    fontSize: "clamp(0.85rem, 1vw, 1.1rem)",
    fontWeight: 600,
    color: "var(--text-dim)",
  },
  footer: {
    padding: "var(--space-md) var(--space-lg)",
    background: "var(--surface)",
    borderTop: "1px solid var(--border)",
    display: "flex",
    justifyContent: "space-between",
    gap: "var(--space-md)",
    alignItems: "center",
  },
  btn: (variant: "primary" | "secondary") => ({
    padding: "0.875rem var(--space-lg)",
    borderRadius: "var(--radius-md)",
    border: "none",
    fontSize: "var(--text-base)",
    fontWeight: 700,
    cursor: "pointer",
    background: variant === "primary" ? "var(--brand)" : "var(--bg-deep)",
    color: variant === "primary" ? "#fff" : "var(--text)",
    minHeight: "52px",
    boxShadow: "var(--shadow-sm)",
  }),
  hint: {
    color: "var(--text-dim)",
    fontSize: "var(--text-xs)",
  },
  centered: {
    textAlign: "center" as const,
    color: "var(--text-mid)",
    fontSize: "var(--text-base)",
  },
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LiveHostGameProps {
  sessionId: string;
  questionCount?: number;
}

function configureSocketAuth(socket: Socket, auth: Record<string, string>): void {
  if (socket.connected && JSON.stringify(socket.auth ?? {}) !== JSON.stringify(auth)) {
    socket.disconnect();
  }
  socket.auth = auth;
}

/**
 * True when the element under the key event handles Space or Enter itself.
 * Advancing on top of it would double-fire the step and swallow the keypress
 * the control was meant to receive.
 */
function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "BUTTON"
    || tag === "INPUT"
    || tag === "TEXTAREA"
    || tag === "SELECT"
    || tag === "A"
    || target.isContentEditable
  );
}

function getRemainingSeconds(phaseEndsAt: number | null): number | null {
  if (phaseEndsAt === null) return null;
  return Math.max(0, Math.ceil((phaseEndsAt - Date.now()) / 1000));
}

function useRemainingSeconds(phaseEndsAt: number | null, active: boolean): number | null {
  const [remaining, setRemaining] = useState<number | null>(() =>
    active ? getRemainingSeconds(phaseEndsAt) : null,
  );

  useEffect(() => {
    if (!active || phaseEndsAt === null) {
      setRemaining(null);
      return;
    }

    const update = () => setRemaining(getRemainingSeconds(phaseEndsAt));
    update();
    const interval = window.setInterval(update, 250);
    return () => window.clearInterval(interval);
  }, [phaseEndsAt, active]);

  return remaining;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LiveHostGame({ sessionId, questionCount: initialQCount = 0 }: LiveHostGameProps) {
  const socketRef = useRef<Socket | null>(null);
  const [state, dispatch] = useReducer(reducer, {
    ...INITIAL,
    questionCount: initialQCount,
  });
  const [isConnected, setIsConnected] = useState(false);
  const t = useT();

  useEffect(() => {
    const socket = getLiveSocket();
    socketRef.current = socket;
    // No token in the handshake: the session cookie is httpOnly, so script
    // cannot read it. The browser attaches it to the WebSocket upgrade and the
    // server reads it from there.
    configureSocketAuth(socket, { type: "host" });

    function onConnect() {
      setIsConnected(true);
      socket.emit("host:join", { sessionId });
    }
    function onDisconnect() {
      setIsConnected(false);
    }
    function onConnectError() {
      setIsConnected(false);
    }
    function onStateChanged(data: { phase: GamePhase; phaseEndsAt?: string | null; serverNow?: string }) {
      dispatch({
        type: "STATE_CHANGED",
        phase: data.phase,
        phaseEndsAt: toLocalPhaseEnd(data.phaseEndsAt, data.serverNow),
      });
    }
    function onQuestionStarted(data: QuestionEvent & { questionCount?: number; questionIndex?: number }) {
      dispatch({
        type: "QUESTION_STARTED",
        question: data,
        questionCount: data.questionCount ?? initialQCount,
        questionIndex: data.orderIndex ?? 0,
      });
    }
    function onTimer(data: { endsAt: string | null; serverNow?: string }) {
      dispatch({ type: "TIMER", phaseEndsAt: toLocalPhaseEnd(data.endsAt, data.serverNow) });
    }
    function onAnswerProgress(data: { answered: number; total: number }) {
      dispatch({ type: "ANSWER_PROGRESS", answered: data.answered, total: data.total });
    }
    function onAnswerDistribution(data: {
      distribution: Record<string, number>;
      optionAnswerers?: Record<string, Answerer[]>;
      correctOptionIds: string[];
      correctTextAnswers?: string[];
      textResults?: TextResultSummary[];
      omittedTextResults?: number;
      noAnswer?: Answerer[];
      noAnswerCount?: number;
    }) {
      dispatch({
        type: "ANSWER_DISTRIBUTION",
        distribution: data.distribution,
        correctOptionIds: data.correctOptionIds,
        ...(data.optionAnswerers ? { optionAnswerers: data.optionAnswerers } : {}),
        ...(data.correctTextAnswers ? { correctTextAnswers: data.correctTextAnswers } : {}),
        ...(data.textResults ? { textResults: data.textResults } : {}),
        ...(data.omittedTextResults !== undefined
          ? { omittedTextResults: data.omittedTextResults }
          : {}),
        ...(data.noAnswer ? { noAnswer: data.noAnswer } : {}),
        ...(data.noAnswerCount !== undefined
          ? { noAnswerCount: data.noAnswerCount }
          : {}),
      });
    }
    function onLeaderboard(data: { rankings: LeaderboardEntry[] }) {
      dispatch({ type: "LEADERBOARD", entries: data.rankings });
    }
    function onPodium(data: { rankings: LeaderboardEntry[] }) {
      dispatch({ type: "PODIUM", entries: data.rankings });
    }
    function onSnapshot(data: {
      phase: GamePhase;
      questionCount?: number;
      questionIndex?: number;
      settings?: { showLeaderboard?: boolean };
    }) {
      if (data.settings?.showLeaderboard !== undefined) {
        dispatch({ type: "SETTINGS", showLeaderboard: data.settings.showLeaderboard });
      }
      if (data.phase === "question_intro" || data.phase === "answering") {
        dispatch({ type: "STATE_CHANGED", phase: data.phase });
      }
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("session:stateChanged", onStateChanged);
    socket.on("question:started", onQuestionStarted);
    socket.on("question:timer", onTimer);
    socket.on("host:answerProgress", onAnswerProgress);
    socket.on("results:answerDistribution", onAnswerDistribution);
    socket.on("leaderboard:updated", onLeaderboard);
    socket.on("session:podium", onPodium);
    socket.on("session:snapshot", onSnapshot);

    if (socket.connected) onConnect();
    else socket.connect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("session:stateChanged", onStateChanged);
      socket.off("question:started", onQuestionStarted);
      socket.off("question:timer", onTimer);
      socket.off("host:answerProgress", onAnswerProgress);
      socket.off("results:answerDistribution", onAnswerDistribution);
      socket.off("leaderboard:updated", onLeaderboard);
      socket.off("session:podium", onPodium);
      socket.off("session:snapshot", onSnapshot);
    };
  }, [sessionId, initialQCount]);

  const emit = useCallback((event: string, payload?: unknown) => {
    socketRef.current?.emit(event, payload ?? {});
  }, []);

  const handleAdvance = useCallback(() => {
    const { phase } = state;
    if (phase === "question_intro" && state.question?.type === "slide") emit("host:openAnswering");
    else if (phase === "answering") return;
    else if (phase === "answer_reveal" && state.showLeaderboard) emit("host:showLeaderboard");
    else if (phase === "answer_reveal" && state.questionCount > 0 && state.questionIndex + 1 >= state.questionCount) {
      emit("host:showPodium");
    } else if (phase === "answer_reveal") emit("host:nextQuestion");
    else if (phase === "leaderboard" && state.questionCount > 0 && state.questionIndex + 1 >= state.questionCount) {
      emit("host:showPodium");
    } else if (phase === "leaderboard") emit("host:nextQuestion");
    else if (phase === "podium") emit("host:endSession");
  }, [emit, state]);

  // Keyboard: Space/Enter advances. The host screen is driven from across the
  // room, so the shortcut is global — but it must not fire on top of a control
  // that already handles the key, or the same step is triggered twice.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== " " && e.key !== "Enter") return;
      if (e.repeat || e.altKey || e.ctrlKey || e.metaKey) return;
      if (isInteractiveTarget(e.target)) return;
      e.preventDefault();
      handleAdvance();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleAdvance]);

  const advanceLabel = (() => {
    switch (state.phase) {
      case "question_intro": return state.question?.type === "slide" ? t("hostGame.continue") : null;
      case "answering": return null;
      case "answer_reveal":
        if (state.showLeaderboard) return t("hostGame.showLeaderboard");
        return state.questionCount > 0 && state.questionIndex + 1 >= state.questionCount
          ? t("hostGame.showFinalResults")
          : t("hostGame.nextQuestion");
      case "leaderboard":
        return state.questionCount > 0 && state.questionIndex + 1 >= state.questionCount
          ? t("hostGame.showFinalResults")
          : t("hostGame.nextQuestion");
      case "podium": return t("hostGame.endSession");
      default: return null;
    }
  })();

  const remainingSeconds = useRemainingSeconds(state.phaseEndsAt, state.phase === "answering");
  const footerHint = state.phase === "answering"
    ? t("hostGame.timerCloses")
    : state.phase === "question_intro" && state.question?.type !== "slide"
    ? t("hostGame.timerStarts")
    : t("hostGame.spaceToAdvance");
  const phaseLabel = ({
    question_intro: t("hostGame.phaseQuestion"),
    answering: t("hostGame.phaseQuestion"),
    answer_reveal: t("hostGame.phaseReveal"),
    leaderboard: t("hostGame.phaseLeaderboard"),
    podium: t("hostGame.phasePodium"),
    ended: t("hostGame.phaseEnded"),
    aborted: t("hostGame.phaseAborted"),
  } as Record<GamePhase, string>)[state.phase];

  return (
    <div style={s.page}>
      {!isConnected && (
        <div
          role="alert"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            padding: "0.5rem 1rem",
            background: "var(--error, #c0392b)",
            color: "#fff",
            textAlign: "center",
            fontSize: "0.875rem",
            fontWeight: 600,
            zIndex: 999,
          }}
        >
          {t("hostGame.connectionLost")}
        </div>
      )}
      <header style={s.header} role="banner">
        <span style={s.headerLeft}>
          {state.questionCount > 0
            ? t("hostGame.questionOf", {
                current: state.questionIndex + 1,
                total: state.questionCount,
              })
            : t("hostGame.questionOfUnknown", { current: state.questionIndex + 1 })}
        </span>
        <span style={s.headerRight}>
          {state.phase === "answering" ? (
            <>
              {t("hostGame.answeredCount", {
                answered: state.answerCount,
                total: state.totalActive,
              })}
              {remainingSeconds !== null && (
                <>
                  {" · "}
                  <span style={s.timerBadge}>
                    {t("hostGame.secondsLeft", { seconds: remainingSeconds })}
                  </span>
                </>
              )}
            </>
          ) : phaseLabel}
        </span>
      </header>

      <main style={s.main}>
        {renderHostContent(state, t)}
      </main>

      <footer style={s.footer}>
        <span style={s.hint}>{footerHint}</span>
        {advanceLabel && (
          <button
            className="host-advance-button"
            style={s.btn("primary")}
            onClick={handleAdvance}
            aria-label={advanceLabel}
          >
            {advanceLabel}
          </button>
        )}
      </footer>
    </div>
  );
}

function renderHostContent(state: HostGameState, t: Translate) {
  const {
    phase,
    question,
    distribution,
    correctOptionIds,
    correctTextAnswers,
    textResults,
    omittedTextResults,
    optionAnswerers,
    noAnswer,
    noAnswerCount,
    leaderboard,
  } = state;

  if (phase === "ended" || phase === "aborted") {
    return (
      <div style={s.centered} role="status">
        <p style={{ fontSize: "var(--text-2xl)", fontWeight: 800, margin: "0 0 0.5rem" }}>
          {t("hostGame.sessionComplete")}
        </p>
        <p style={{ margin: 0, color: "var(--text-mid)" }}>{t("hostGame.greatGame")}</p>
      </div>
    );
  }

  if (phase === "podium" && leaderboard.length > 0) {
    return <PodiumCeremony entries={leaderboard} />;
  }

  if (phase === "leaderboard" && leaderboard.length > 0) {
    return (
      <LeaderboardView key="leaderboard" entries={leaderboard} title={t("hostGame.leaderboard")} t={t} />
    );
  }

  if (phase === "answer_reveal" && question && distribution) {
    return (
      <>
        <h1 style={s.prompt}>{question.prompt}</h1>
        {question.imageAssetId && (
          <img
            src={`/media/${question.imageAssetId}`}
            alt={t("play.questionImage", { prompt: question.prompt })}
            style={s.questionImage}
          />
        )}
        {question.type === "type_answer" ? (
          <TextDistributionView
            correctTextAnswers={correctTextAnswers}
            textResults={textResults}
            omittedTextResults={omittedTextResults}
            t={t}
          />
        ) : (
          <DistributionView
            question={question}
            distribution={distribution}
            optionAnswerers={optionAnswerers}
            correctOptionIds={correctOptionIds}
            t={t}
          />
        )}
        <NoAnswerView answerers={noAnswer} total={noAnswerCount} t={t} />
      </>
    );
  }

  if (!question) {
    return (
      <div style={s.centered} role="status">
        {t("hostGame.waitingToStart")}
      </div>
    );
  }

  return (
    <>
      <h1 style={s.prompt}>{question.prompt}</h1>
      {question.imageAssetId && (
        <img
          src={`/media/${question.imageAssetId}`}
          alt={t("play.questionImage", { prompt: question.prompt })}
          style={s.questionImage}
        />
      )}
      {phase === "answering" && (
        <div style={{ display: "grid", justifyItems: "center", gap: "0.5rem" }}>
          {state.phaseEndsAt !== null && question.timerSeconds > 0 && (
            <CountdownRing
              endsAt={state.phaseEndsAt}
              totalSeconds={question.timerSeconds}
              size={84}
            />
          )}
          <p style={{ ...s.answerProgress, margin: 0 }} role="status" aria-live="polite">
            {t("hostGame.answeredProgress", {
              answered: state.answerCount,
              total: state.totalActive,
            })}
          </p>
        </div>
      )}
      {question.type === "slide" ? (
        <p style={s.centered}>{t("hostGame.continueReady")}</p>
      ) : (
        <OptionsPreview question={question} t={t} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// OptionsPreview
// ---------------------------------------------------------------------------

function OptionsPreview({ question, t }: { question: QuestionEvent; t: Translate }) {
  if (question.options.length === 0) {
    return <p style={s.centered}>{t("hostGame.playersWillType")}</p>;
  }

  return (
    <div className="answers-grid-host" role="group" aria-label={t("play.answerOptions")}>
      {question.options.map((opt, i) => (
        <div key={opt.id} style={s.optionBar(OPTION_COLORS[i % OPTION_COLORS.length] ?? "var(--text-mid)", null)}>
          <div style={s.optionLeft}>
            <span style={s.optionLetter} aria-hidden="true">
              <OptionShape index={i} size={24} />
            </span>
            <span style={s.optionLabel}>{opt.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DistributionView
// ---------------------------------------------------------------------------

/** Response time, host-legible at a distance: 1100ms reads as "1.1s". */
function formatResponseTime(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * Names of the players behind one answer group. The server sends at most
 * MAX_ANSWERERS_PER_GROUP names (fastest first) while `total` stays the true
 * count, so a large room degrades to "first N, then +M more" instead of an
 * unbounded list that would overflow the shared display.
 */
/**
 * Names shown per answer. The strip is clipped so it can never push the grid
 * off a shared display, so rendering more than fits would silently drop them —
 * this keeps the rendered count inside the clip and reports the rest.
 */
export const MAX_ANSWERERS_SHOWN = 6;

/** How long each page of names holds before the next set pours in. */
export const ANSWERER_ROTATION_MS = 2600;

/** Delay between one chip landing and the next, in ms. */
const POUR_STAGGER_MS = 70;

function AnswererStrip({
  label,
  answerers,
  total,
  t,
}: {
  label: string;
  answerers: Answerer[];
  total: number;
  t: Translate;
}) {
  const start = useRotatingWindow(answerers.length, MAX_ANSWERERS_SHOWN, ANSWERER_ROTATION_MS);
  if (answerers.length === 0) return null;

  const shown = answerers.slice(start, start + MAX_ANSWERERS_SHOWN);
  // Counts the names this strip will never reach, not the ones off this page —
  // the rotation brings the rest around on its own.
  const unreachable = Math.max(0, total - answerers.length);

  return (
    <>
      <ul style={s.answererStrip} aria-label={label}>
        {shown.map((a, i) => (
          <li
            // Keyed by slot as well as player so a rotation replays the pour
            // instead of React reusing the settled chip in place.
            key={`${start}-${a.playerId}`}
            className="answerer-chip"
            style={{ ...s.answererChip, animationDelay: `${i * POUR_STAGGER_MS}ms` }}
          >
            <span style={s.answererName}>{a.nickname}</span>
            {a.responseTimeMs !== undefined && (
              <span style={s.answererTime}>{formatResponseTime(a.responseTimeMs)}</span>
            )}
          </li>
        ))}
      </ul>
      {/* Outside the clipped list on purpose: as its last child it was the
          first thing to be cut off, which is exactly when it is needed. */}
      {unreachable > 0 && (
        <p style={s.answererMore}>{t("hostGame.andMore", { count: unreachable })}</p>
      )}
    </>
  );
}

/**
 * The players who let the clock run out. They are the one group the grid above
 * cannot account for, because they never picked an answer to sit under.
 */
function NoAnswerView({
  answerers,
  total,
  t,
}: {
  answerers: Answerer[];
  total: number;
  t: Translate;
}) {
  if (total === 0) return null;
  return (
    <section style={s.noAnswerBlock} aria-label={t("hostGame.playersWhoDidntAnswer")}>
      <p style={s.noAnswerHeading}>
        {t("hostGame.didntAnswer")} <span style={s.noAnswerCount}>{total}</span>
      </p>
      <AnswererStrip
        label={t("hostGame.playersWhoDidntAnswer")}
        answerers={answerers}
        total={total}
        t={t}
      />
    </section>
  );
}

interface DistributionViewProps {
  question: QuestionEvent;
  distribution: Record<string, number>;
  optionAnswerers: Record<string, Answerer[]>;
  correctOptionIds: string[];
  t: Translate;
}

function DistributionView({
  question,
  distribution,
  optionAnswerers,
  correctOptionIds,
  t,
}: DistributionViewProps) {
  return (
    <div className="answers-grid-host" role="group" aria-label={t("hostGame.answerDistribution")}>
      {question.options.map((opt, i) => {
        const count = distribution[opt.id] ?? 0;
        const isCorrect = correctOptionIds.includes(opt.id);
        return (
          <div key={opt.id} style={s.optionBlock}>
            <div style={s.optionBar(OPTION_COLORS[i % OPTION_COLORS.length] ?? "var(--text-mid)", isCorrect)}>
              <div style={s.optionLeft}>
                <span style={s.optionLetter} aria-hidden="true">
                  <OptionShape index={i} size={24} />
                </span>
                <span style={s.optionLabel}>{opt.label}</span>
                {/* Green alone does not tell a colour-blind room which answer
                    was right, and the shared display has no hover or focus. */}
                {isCorrect && <span style={s.correctBadge}>{t("hostGame.correct")}</span>}
              </div>
              <span className="host-option-count" style={s.optionCount}>
                {count}
                <span className="visually-hidden">
                  {count === 1 ? t("hostGame.oneAnswer") : t("hostGame.answerCount")}
                </span>
              </span>
            </div>
            <AnswererStrip
              label={t("hostGame.playersWhoChose", { label: opt.label })}
              answerers={optionAnswerers[opt.id] ?? []}
              total={count}
              t={t}
            />
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TextDistributionView
// ---------------------------------------------------------------------------

function TextDistributionView({
  correctTextAnswers,
  textResults,
  omittedTextResults,
  t,
}: {
  correctTextAnswers: string[];
  textResults: TextResultSummary[];
  omittedTextResults: number;
  t: Translate;
}) {
  return (
    <>
      {correctTextAnswers.length > 0 && (
        <p style={s.acceptedAnswers}>
          {t("hostGame.accepted", { answers: correctTextAnswers.join(", ") })}
        </p>
      )}
      {textResults.length === 0 ? (
        <p style={s.centered}>{t("hostGame.noAnswersSubmitted")}</p>
      ) : (
        <ul style={s.textAnswerList} aria-label={t("hostGame.typedAnswerResults")}>
          {textResults.map((result, index) => (
            <li
              key={`${result.submittedText ?? "blank"}-${index}`}
              style={s.textAnswerRow(result.isCorrect)}
            >
              <div style={s.textAnswerTop}>
                <span style={s.textAnswerValue}>
                  {result.submittedText || t("hostGame.blankAnswer")}
                  {result.isCorrect && (
                    <span style={s.textAnswerCorrectBadge}>{t("hostGame.correct")}</span>
                  )}
                </span>
                <span style={s.textAnswerCount}>
                  {result.count === 1
                    ? t("hostGame.oneAnswerWithCount")
                    : t("hostGame.answersWithCount", { count: result.count })}
                </span>
              </div>
              <AnswererStrip
                label={t("hostGame.playersWhoTyped", {
                  text: result.submittedText || t("hostGame.blankAnswer"),
                })}
                answerers={result.answerers ?? []}
                total={result.count}
                t={t}
              />
            </li>
          ))}
        </ul>
      )}
      {omittedTextResults > 0 && (
        <p style={s.acceptedAnswers}>
          {omittedTextResults === 1
            ? t("hostGame.oneMoreAnswerHidden")
            : t("hostGame.moreAnswersHidden", { count: omittedTextResults })}
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// ScoreDisplay
// ---------------------------------------------------------------------------

function ScoreDisplay({ score, delay }: { score: number; delay: number }) {
  const displayed = useCountUp(score, delay);
  return <>{displayed.toLocaleString()} pts</>;
}

// ---------------------------------------------------------------------------
// LeaderboardView
// ---------------------------------------------------------------------------

/** Ranks shown on a board. Ten fits any presenter's screen without scrolling. */
export const LEADERBOARD_SIZE = 10;

function LeaderboardView({
  entries,
  title,
  t,
}: {
  entries: LeaderboardEntry[];
  title: string;
  t: Translate;
}) {
  const leaders = entries.slice(0, LEADERBOARD_SIZE);
  const others = Math.max(0, entries.length - leaders.length);

  const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
  const STAGGER = 35;
  const ROW_DURATION = 240;
  const RANK_DURATION = 280;

  return (
    <>
      <h1
        style={{
          ...s.prompt,
          animation: `slide-up-in 220ms ${EASE} both`,
        }}
      >
        {title}
      </h1>
      <ol style={s.leaderboardList} aria-label={title}>
        {leaders.map((entry, i) => {
          const rowDelay = 60 + i * STAGGER;
          const isTopThree = entry.rank <= 3;
          return (
            <li
              key={entry.playerId}
              style={{
                ...s.leaderboardRow(entry.rank),
                animation: `slide-up-in ${ROW_DURATION}ms ${EASE} ${rowDelay}ms both`,
              }}
            >
              <span
                style={{
                  ...s.leaderboardRank,
                  ...(isTopThree && {
                    animation: `rank-pop ${RANK_DURATION}ms ${EASE} ${rowDelay + 60}ms both`,
                    display: "inline-block",
                  }),
                }}
              >
                #{entry.rank}
              </span>
              <span style={s.leaderboardNickname}>{entry.nickname}</span>
              <span style={s.leaderboardScore}>
                <ScoreDisplay score={entry.score} delay={rowDelay + 80} />
              </span>
            </li>
          );
        })}
      </ol>
      {others > 0 && (
        <p style={s.boardFooter}>
          {others === 1
            ? t("hostGame.oneMorePlayerHidden")
            : t("hostGame.morePlayersHidden", { count: others })}
        </p>
      )}
    </>
  );
}
